import { AlignRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import AnimatedContent from "./AnimatedContent";

import headerLogo from "@/assets/header-logo.webp";
import contactImage from "@/assets/contact-button-icon.webp";

export default function Header() {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        isOpen &&
        headerRef.current &&
        !headerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const whatsappMessage = encodeURIComponent(t("contact.whatsappMessage"));
  const whatsappHref = `https://wa.me/5511967611652?text=${whatsappMessage}`;

  return (
    <AnimatedContent reverse delay={0.2}>
      <header
        ref={headerRef}
        className="flex flex-col text-ink fixed pt-6 w-full px-4 md:px-8 xl:px-16 z-10 items-center"
      >
        <div className="w-full flex justify-between items-center bg-gradient-overlay backdrop-blur-md px-6 pr-4 p-2 border-line border-2 rounded-full shadow-[0_8px_24px_rgba(11,27,58,0.06)] dark:border-[#2F2F2F4D] dark:shadow-none md:pl-8 md:pr-2 z-10">
          <Link to="/" aria-label={t("common.home")}>
            <span
              role="img"
              aria-label={t("header.logoAlt")}
              className="logo-brand block w-16 h-[26px]"
              style={{
                WebkitMaskImage: `url(${headerLogo})`,
                maskImage: `url(${headerLogo})`,
              }}
            />
          </Link>

          <nav
            className=" hidden md:flex gap-6 font-light"
            aria-label={t("header.mainNav")}
          >
            <Link to="/" className="text-ink-muted hover:text-brand dark:text-[#bbb] dark:hover:text-white">
              {t("common.home")}
            </Link>
            <Link to="/portfolio" className="text-ink-muted hover:text-brand dark:text-[#bbb] dark:hover:text-white">
              {t("common.portfolio")}
            </Link>
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <LanguageSwitcher />
            <ThemeToggle />
            <a
              href={whatsappHref}
              className="flex items-center p-2 px-4 gap-2 rounded-full text-on-brand bg-brand hover:bg-brand-deep transition-colors dark:text-[#121212] dark:bg-white dark:hover:bg-[#E4E5E6]"
              aria-label={t("header.contact")}
            >
              <span className="font-normal">{t("header.contact")}</span>
              <img
                src={contactImage}
                alt={t("header.contactIconAlt")}
                width={32}
                height={33}
                className="w-4 icon-on-brand"
              />
            </a>
          </div>

          <div className="flex md:hidden items-center gap-3">
            <ThemeToggle />

            <span
              role="button"
              aria-label={t("header.openMenu")}
              className="flex cursor-pointer"
              onClick={() => setIsOpen(!isOpen)}
            >
              <AlignRight className="text-brand dark:text-[#4C4C4C]" />
            </span>
          </div>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="w-full flex md:hidden px-4 mt-4 py-6 rounded-2xl bg-gradient-overlay backdrop-blur-md dark:backdrop-blur-sm border-2 border-line shadow-[0_8px_24px_rgba(11,27,58,0.08)] dark:border-[#2F2F2F4D] dark:shadow-none flex-col gap-4"
            >
              <nav
                className="flex flex-col w-full gap-6"
                aria-label={t("header.mobileNav")}
              >
                <Link
                  to="/"
                  className="text-ink-muted hover:text-brand dark:text-white"
                  onClick={() => setIsOpen(false)}
                >
                  {t("common.home")}
                </Link>

                <div className="w-full border-t border-line border-1"></div>

                <Link
                  to="/portfolio"
                  className="text-ink-muted hover:text-brand dark:text-white"
                  onClick={() => setIsOpen(false)}
                >
                  {t("common.portfolio")}
                </Link>

                <div className="w-full border-t border-line border-1"></div>

                <div className="flex justify-between items-center gap-4">
                  <LanguageSwitcher />
                  <a
                    href={whatsappHref}
                    className="flex items-center p-2 px-4 gap-2 rounded-full text-on-brand bg-brand hover:bg-brand-deep transition-colors dark:text-[#121212] dark:bg-white dark:hover:bg-[#E4E5E6]"
                    aria-label={t("header.contact")}
                    onClick={() => setIsOpen(false)}
                  >
                    <span className="font-normal">{t("header.contact")}</span>
                    <img
                      src={contactImage}
                      alt={t("header.contactIconAlt")}
                      width={32}
                      height={33}
                      className="w-4 icon-on-brand"
                    />
                  </a>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </AnimatedContent>
  );
}
